import type { ProjectEntry } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

const MAX_CONCURRENT_PARTS = 3;

type CreatedUpload = Awaited<
  ReturnType<typeof orpc.projects.uploads.create.call>
>;

export type ProjectUploadProgress =
  | { phase: 'preparing'; loadedBytes: 0; totalBytes: number }
  | { phase: 'uploading'; loadedBytes: number; totalBytes: number }
  | { phase: 'finalizing'; loadedBytes: number; totalBytes: number };

export interface UploadProjectFileOptions {
  signal?: AbortSignal;
  onProgress?: (progress: ProjectUploadProgress) => void;
}

interface PutBlobOptions {
  url: string;
  body: Blob;
  contentType?: string;
  signal?: AbortSignal;
  onProgress: (loadedBytes: number) => void;
}

function cancelledError(): DOMException {
  return new DOMException('Upload cancelled', 'AbortError');
}

function putBlob({
  url,
  body,
  contentType,
  signal,
  onProgress,
}: PutBlobOptions): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(cancelledError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener('abort', abort);

    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(body.size);
        resolve(xhr.getResponseHeader('etag'));
        return;
      }
      reject(new Error(`Upload failed (HTTP ${String(xhr.status)})`));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error('Upload failed because of a network error'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(cancelledError());
    };
    signal?.addEventListener('abort', abort, { once: true });
    xhr.send(body);
  });
}

async function uploadSingle(
  file: File,
  created: CreatedUpload,
  options: UploadProjectFileOptions,
): Promise<ProjectEntry> {
  if (!created.uploadUrl) throw new Error('Upload URL was not returned');
  await putBlob({
    url: created.uploadUrl,
    body: file,
    contentType: file.type || 'application/octet-stream',
    signal: options.signal,
    onProgress: (loadedBytes) =>
      options.onProgress?.({
        phase: 'uploading',
        loadedBytes,
        totalBytes: file.size,
      }),
  });
  options.onProgress?.({
    phase: 'finalizing',
    loadedBytes: file.size,
    totalBytes: file.size,
  });
  return orpc.projects.uploads.complete.call({
    uploadId: created.uploadId,
    parts: [],
  });
}

async function getPartUrls(
  created: CreatedUpload,
  partCount: number,
): Promise<Map<number, string>> {
  const urls = new Map(
    created.partUrls.map((part) => [part.partNumber, part.url]),
  );
  const missing = Array.from(
    { length: partCount },
    (_, index) => index + 1,
  ).filter((partNumber) => !urls.has(partNumber));
  for (let offset = 0; offset < missing.length; offset += 100) {
    const signed = await orpc.projects.uploads.signParts.call({
      uploadId: created.uploadId,
      partNumbers: missing.slice(offset, offset + 100),
    });
    for (const part of signed) urls.set(part.partNumber, part.url);
  }
  return urls;
}

async function uploadMultipart(
  file: File,
  created: CreatedUpload,
  options: UploadProjectFileOptions,
): Promise<ProjectEntry> {
  const reportedPartSize = created.partSizeBytes;
  if (!reportedPartSize)
    throw new Error('Multipart upload size was not returned');
  const partSize: number = reportedPartSize;
  const partCount = Math.ceil(file.size / partSize);
  const urls = await getPartUrls(created, partCount);
  const loadedByPart = new Map<number, number>();
  const parts: Array<{ partNumber: number; etag: string }> = [];
  const multipartController = new AbortController();
  const cancelMultipart = () => multipartController.abort();
  options.signal?.addEventListener('abort', cancelMultipart, { once: true });
  if (options.signal?.aborted) multipartController.abort();

  let nextPartNumber = 1;
  const reportProgress = (partNumber: number, loadedBytes: number) => {
    loadedByPart.set(partNumber, loadedBytes);
    const loaded = Array.from(loadedByPart.values()).reduce(
      (total, partLoaded) => total + partLoaded,
      0,
    );
    options.onProgress?.({
      phase: 'uploading',
      loadedBytes: Math.min(loaded, file.size),
      totalBytes: file.size,
    });
  };

  async function worker(): Promise<void> {
    while (nextPartNumber <= partCount) {
      const partNumber = nextPartNumber;
      nextPartNumber += 1;
      const url = urls.get(partNumber);
      if (!url) throw new Error(`Missing URL for part ${String(partNumber)}`);
      const start = (partNumber - 1) * partSize;
      const body = file.slice(start, Math.min(file.size, start + partSize));
      const etag = await putBlob({
        url,
        body,
        signal: multipartController.signal,
        onProgress: (loadedBytes) => reportProgress(partNumber, loadedBytes),
      });
      if (!etag) throw new Error(`Part ${String(partNumber)} has no ETag`);
      parts.push({ partNumber, etag });
    }
  }

  try {
    await Promise.all(
      Array.from({ length: Math.min(partCount, MAX_CONCURRENT_PARTS) }, () =>
        worker(),
      ),
    );
  } catch (error) {
    multipartController.abort();
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', cancelMultipart);
  }

  options.onProgress?.({
    phase: 'finalizing',
    loadedBytes: file.size,
    totalBytes: file.size,
  });
  return orpc.projects.uploads.complete.call({
    uploadId: created.uploadId,
    parts: parts.sort((left, right) => left.partNumber - right.partNumber),
  });
}

export async function uploadProjectFile(
  projectId: string,
  parentEntryId: string | null,
  file: File,
  options: UploadProjectFileOptions = {},
): Promise<ProjectEntry> {
  options.onProgress?.({
    phase: 'preparing',
    loadedBytes: 0,
    totalBytes: file.size,
  });
  if (options.signal?.aborted) throw cancelledError();

  const created = await orpc.projects.uploads.create.call({
    projectId,
    parentEntryId,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  });
  try {
    return created.mode === 'single'
      ? await uploadSingle(file, created, options)
      : await uploadMultipart(file, created, options);
  } catch (error) {
    await orpc.projects.uploads.abort
      .call({ uploadId: created.uploadId })
      .catch(() => undefined);
    throw error;
  }
}
